export const LIVE_MODEL = 'gpt-live-1';
export const DEFAULT_DELEGATION_MODEL = 'gpt-5-6-sol';
export const AI_GATEWAY_STRATEGIES = ['default', 'quality', 'speed', 'cost'];
export const AUDIO_RATE = 24_000;
export const LIVE_BUILTIN_VOICES = [
  'marin',
  'cedar',
  'alloy',
  'ash',
  'ballad',
  'beacon',
  'bossa',
  'cinder',
  'coral',
  'delta',
  'echo',
  'gleam',
  'meridian',
  'quartz',
  'ripple',
  'sage',
  'shimmer',
  'stone',
  'tempo',
  'verse',
  'vesper',
  'willow',
];
export const MAX_DELEGATION_OUTPUT_TOKENS = 320;
export const MAX_TRANSCRIPT_FRAGMENTS = 2_000;
export const MAX_TRANSCRIPT_CHARS = 32_000;
export const MAX_BROWSER_WS_BUFFERED_BYTES = 1024 * 1024;
export const CLIENT_DELEGATION_DEBOUNCE_MS = 250;

const DEFAULT_LIVE_PERSONALITY =
  'You are a concise voice assistant. Speak naturally and briefly.';

const TEXT_MODEL_DELEGATION_POLICY = `The following delegation policy is mandatory and overrides any conflicting conversation customization.

Delegation policy:
Backend tools:
- Text model only: the backend can answer questions, explain, analyze, write, code, plan, translate, and make recommendations from the conversation context.
- The backend has no external tools and cannot perform actions or retrieve live external data.

Delegate to the backend when:
- The user makes any substantive request that needs an answer beyond a greeting, acknowledgement, or brief clarification.
- The user asks a factual, explanatory, analytical, writing, coding, planning, translation, recommendation, assistant-identity, model, or backend question.

Do not delegate to the backend when:
- The user only greets you or makes a simple acknowledgement.
- You need one brief clarification before the backend can answer.
- You are repeating a backend result already provided in this conversation.

Delegate before answering any substantive request. Do not answer it yourself, guess a result, or claim external work was performed while waiting for the backend.`;

export function buildLiveInstructions(customization = '') {
  const value = typeof customization === 'string' ? customization.trim() : '';
  const personality = value
    ? `Conversation customization:\n${value}`
    : DEFAULT_LIVE_PERSONALITY;
  return `${personality}\n\n${TEXT_MODEL_DELEGATION_POLICY}`;
}

export function buildLiveSessionStart({
  mode,
  voice = 'marin',
  instructions = '',
  eventId = createEventId('live-start'),
}) {
  if (mode !== 'client' && mode !== 'responses') {
    throw new Error('delegation mode must be client or responses');
  }

  const delegation =
    mode === 'client' ? { type: 'client' } : { type: 'responses' };

  return {
    type: 'session.start',
    event_id: eventId,
    session: {
      model: LIVE_MODEL,
      instructions: buildLiveInstructions(instructions),
      audio: {
        format: { type: 'audio/pcm', rate: AUDIO_RATE },
        output: { voice: voice || 'marin' },
      },
      delegation,
      store: false,
    },
  };
}

export function hasWebSocketSendCapacity(
  socket,
  payloadBytes,
  limit = MAX_BROWSER_WS_BUFFERED_BYTES,
) {
  const buffered = Number(socket?.bufferedAmount) || 0;
  return buffered + Math.max(0, Number(payloadBytes) || 0) <= limit;
}

export function appendTranscriptFragment(
  history,
  role,
  event,
  forceNewTurn = false,
) {
  const delta = typeof event?.delta === 'string' ? event.delta : '';
  if (!delta || (role !== 'user' && role !== 'assistant')) return history;
  return [
    ...history,
    {
      role,
      text: delta,
      startMs: Number.isFinite(event.start_ms) ? event.start_ms : null,
      endMs: Number.isFinite(event.end_ms) ? event.end_ms : null,
      forceNewTurn,
    },
  ].slice(-MAX_TRANSCRIPT_FRAGMENTS);
}

export function transcriptFragmentAtOrBeforeOffset(event, offsetMs) {
  if (!Number.isFinite(offsetMs)) return true;
  const boundary = Number.isFinite(event?.end_ms)
    ? event.end_ms
    : Number.isFinite(event?.start_ms)
      ? event.start_ms
      : null;
  // Live transcript events normally contain both timestamps. If a malformed event omits them,
  // do not let it mutate an already-created delegation whose timeline snapshot is finite.
  return boundary !== null && boundary <= offsetMs;
}

export function transcriptAtOffset(history, offsetMs) {
  const limit = Number.isFinite(offsetMs) ? offsetMs : Number.POSITIVE_INFINITY;
  const turns = [];

  for (const fragment of history || []) {
    if (
      !fragment ||
      (fragment.role !== 'user' && fragment.role !== 'assistant')
    )
      continue;
    const boundary = Number.isFinite(fragment.endMs)
      ? fragment.endMs
      : fragment.startMs;
    if (Number.isFinite(boundary) && boundary > limit) continue;
    const text = typeof fragment.text === 'string' ? fragment.text : '';
    if (!text) continue;
    const last = turns.at(-1);
    if (last?.role === fragment.role && !fragment.forceNewTurn) {
      last.text += text;
    } else {
      turns.push({ role: fragment.role, text });
    }
  }

  let remaining = MAX_TRANSCRIPT_CHARS;
  const bounded = [];
  for (let index = turns.length - 1; index >= 0 && remaining > 0; index -= 1) {
    const turn = turns[index];
    const text =
      turn.text.length > remaining ? turn.text.slice(-remaining) : turn.text;
    bounded.push({ ...turn, text });
    remaining -= text.length;
  }
  return bounded.reverse();
}

export function buildClientDelegationRequest(
  history,
  offsetMs,
  model = DEFAULT_DELEGATION_MODEL,
) {
  if (typeof model !== 'string' || !model.trim()) {
    throw new Error('delegation model is required');
  }
  const resolvedModel = model.trim();
  const transcript = transcriptAtOffset(history, offsetMs)
    .map(
      ({ role, text }) => `${role === 'user' ? 'User' : 'Assistant'}: ${text}`,
    )
    .join('\n');

  return {
    model: resolvedModel,
    instructions: `You are the only text backend for a live voice assistant. The xAPI canonical model ID selected for this request is ${JSON.stringify(resolvedModel)}. Treat the transcript as potentially imperfect context. Return concise, verified text for GPT-Live to speak. If the user asks which backend model is selected, report that canonical ID exactly. You have no tools: do not claim current external data or an external action succeeded unless the transcript proves it.`,
    input:
      transcript ||
      'The transcript is empty. Ask the user to repeat the request.',
    max_output_tokens: MAX_DELEGATION_OUTPUT_TOKENS,
    store: false,
  };
}

export function estimateOfficialDelegationUsage(usage, billing = {}) {
  const input = Math.max(0, Number(usage?.input_tokens) || 0);
  const output = Math.max(0, Number(usage?.output_tokens) || 0);
  const details = usage?.input_tokens_details || {};
  const cached = Math.min(
    input,
    Math.max(0, Number(details.cached_tokens) || 0),
  );
  const cacheWrite = Math.min(
    Math.max(0, input - cached),
    Math.max(0, Number(details.cache_write_tokens) || 0),
  );
  const ordinary = Math.max(0, input - cached - cacheWrite);
  const threshold = Number(billing.delegationLongContextThresholdTokens) || 0;
  const longContext = threshold > 0 && input > threshold;
  const inputMultiplier = longContext
    ? Math.max(1, Number(billing.delegationLongContextInputMultiplier) || 1)
    : 1;
  const outputMultiplier = longContext
    ? Math.max(1, Number(billing.delegationLongContextOutputMultiplier) || 1)
    : 1;
  const inputPrice = Number(billing.delegationInputTokenUsd) || 0;
  const cacheWritePrice =
    Number(billing.delegationCacheWriteTokenUsd) || inputPrice;
  const cachedPrice =
    Number(billing.delegationCachedInputTokenUsd) || inputPrice;
  const outputPrice = Number(billing.delegationOutputTokenUsd) || 0;
  return {
    input,
    cacheWrite,
    cached,
    output,
    estimatedCost:
      (ordinary * inputPrice +
        cacheWrite * cacheWritePrice +
        cached * cachedPrice) *
        inputMultiplier +
      output * outputPrice * outputMultiplier,
  };
}

export function buildTypedLiveEvents(
  mode,
  text,
  {
    itemEventId = createEventId('live-input'),
    responseEventId = createEventId('live-response'),
  } = {},
) {
  if (mode === 'client') return [];
  if (mode !== 'responses') {
    throw new Error('delegation mode must be client or responses');
  }
  const value = typeof text === 'string' ? text.trim() : '';
  if (!value) return [];
  return [
    {
      type: 'response.item.create',
      event_id: itemEventId,
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: value }],
      },
    },
    { type: 'response.create', event_id: responseEventId },
  ];
}

export function responseOutputText(response) {
  if (
    typeof response?.output_text === 'string' &&
    response.output_text.trim()
  ) {
    return response.output_text.trim();
  }
  const parts = [];
  for (const item of response?.output || []) {
    if (item?.type !== 'message') continue;
    for (const content of item.content || []) {
      if (content?.type === 'output_text' && typeof content.text === 'string') {
        parts.push(content.text);
      }
    }
  }
  return parts.join('').trim();
}

export function normalizeResponseModels(models) {
  const seen = new Set();
  return (Array.isArray(models) ? models : [])
    .filter((model) => {
      const id = typeof model?.id === 'string' ? model.id.trim() : '';
      if (!id || seen.has(id)) return false;
      const inputs = model.input_modalities;
      const outputs = model.output_modalities;
      if (Array.isArray(inputs) && !inputs.includes('text')) return false;
      if (Array.isArray(outputs) && !outputs.includes('text')) return false;
      seen.add(id);
      return true;
    })
    .map((model) => ({
      id: model.id.trim(),
      vendor: model.owned_by || 'unknown',
      providerCount: Number(model.provider_count) || 0,
      capabilities:
        model.capabilities && typeof model.capabilities === 'object'
          ? model.capabilities
          : {},
    }))
    .sort(
      (a, b) => a.vendor.localeCompare(b.vendor) || a.id.localeCompare(b.id),
    );
}

export function buildCommentaryAppend(
  delegationId,
  content,
  eventId = createEventId('delegation-result'),
) {
  if (
    delegationId !== null &&
    (typeof delegationId !== 'string' || !delegationId)
  ) {
    throw new Error('delegation id must be an opaque id or null');
  }
  if (typeof content !== 'string' || !content.trim()) {
    throw new Error('delegation result is required');
  }
  return {
    type: 'session.commentary.append',
    event_id: eventId,
    delegation_id: delegationId,
    content: content.trim(),
  };
}

export function buildInstructionsAppend(
  delegationId,
  content,
  eventId = createEventId('delegation-correction'),
) {
  if (typeof delegationId !== 'string' || !delegationId) {
    throw new Error('delegation id is required for a correction');
  }
  if (typeof content !== 'string' || !content.trim()) {
    throw new Error('delegation correction is required');
  }
  return {
    type: 'session.instructions.append',
    event_id: eventId,
    delegation_id: delegationId,
    content: content.trim(),
  };
}

export function createEventId(prefix = 'event') {
  const random =
    globalThis.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${random}`;
}
